/**
 * Custom hooks for modal/dialog components
 *
 * These hooks encapsulate common modal behaviors:
 * - Keyboard event handling (ESC, Enter)
 * - Body scroll locking
 * - Focus management
 */

import { useEffect, useRef } from 'react';

/**
 * Hook to handle ESC key press
 * @param {boolean} isActive - Whether the handler should be active
 * @param {function} onEscape - Callback when ESC is pressed
 */
export const useEscapeKey = (isActive, onEscape) => {
  useEffect(() => {
    if (!isActive) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onEscape();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isActive, onEscape]);
};

/**
 * Hook to handle Enter key press
 * @param {boolean} isActive - Whether the handler should be active
 * @param {function} onEnter - Callback when Enter is pressed
 * @param {boolean} disabled - Whether the handler is disabled
 */
export const useEnterKey = (isActive, onEnter, disabled = false) => {
  useEffect(() => {
    if (!isActive || disabled) return;

    const handleEnter = (e) => {
      if (e.key === 'Enter') {
        onEnter();
      }
    };

    document.addEventListener('keydown', handleEnter);
    return () => document.removeEventListener('keydown', handleEnter);
  }, [isActive, onEnter, disabled]);
};

/**
 * Hook to lock body scroll when modal is open
 * @param {boolean} isOpen - Whether the modal is open
 */
export const useBodyScrollLock = (isOpen) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);
};

/**
 * Hook to manage focus when modal opens/closes. Shifts focus to the modal element when it opens.
 * Restores focus to the previously active element when modal closes.
 * @param {boolean} isOpen - Whether the modal is open
 * @param {object} elementRef - Ref to the element to focus when modal opens
 * @param {boolean} shouldFocus - Whether to focus the element
 */
export const useModalFocus = (isOpen, elementRef, shouldFocus = true) => {
  const previousActiveElement = useRef();

  useEffect(() => {
    if (isOpen && shouldFocus) {
      // Store previous focus
      previousActiveElement.current = document.activeElement;

      // Focus the modal element
      setTimeout(() => {
        elementRef.current?.focus();
      }, 100);

      return () => {
        // Restore previous focus when modal closes
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen, shouldFocus, elementRef]);
};

/**
 * Combined hook for common modal behaviors
 * @param {object} options - Configuration options
 * @param {boolean} options.isOpen - Whether the modal is open
 * @param {function} options.onClose - Callback to close the modal
 * @param {object} options.elementRef - Ref to the modal element
 * @param {boolean} options.closeOnEscape - Whether ESC should close modal (default: true)
 * @param {boolean} options.lockScroll - Whether to lock body scroll (default: true)
 * @param {boolean} options.manageFocus - Whether to manage focus (default: true)
 */
export const useModal = ({
  isOpen,
  onClose,
  elementRef,
  closeOnEscape = true,
  lockScroll = true,
  manageFocus = true
}) => {
  // Lock body scroll
  useBodyScrollLock(isOpen && lockScroll);

  // Handle ESC key
  useEscapeKey(isOpen && closeOnEscape, onClose);

  // Manage focus
  useModalFocus(isOpen, elementRef, manageFocus);
};

/**
 * Hook for confirm dialog with keyboard shortcuts
 * @param {object} options - Configuration options
 * @param {boolean} options.isOpen - Whether the dialog is open
 * @param {function} options.onClose - Callback to close
 * @param {function} options.onConfirm - Callback to confirm
 * @param {object} options.elementRef - Ref to the dialog element
 * @param {boolean} options.requireTextConfirmation - Whether text confirmation is required
 * @param {boolean} options.loading - Whether the dialog is in loading state
 */
export const useConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  elementRef,
  requireTextConfirmation = false,
  loading = false
}) => {
  // Lock body scroll
  useBodyScrollLock(isOpen);

  // Handle ESC key to close
  useEscapeKey(isOpen, onClose);

  // Handle Enter key to confirm (only if no text confirmation required)
  useEnterKey(isOpen, onConfirm, requireTextConfirmation || loading);

  // Manage initial focus
  useModalFocus(isOpen, elementRef, true);
};
